import { Column } from "primereact/column";
import {
  DataTable,
  DataTablePageEvent,
  DataTableSortEvent,
} from "primereact/datatable";
import { CompanyEntry, configProps } from "../../types";
import React, { useState } from "react";
import { useAppDispatch } from "../../redux/store";
import { Button } from "primereact/button";
import { addNewSlice } from "../../redux/slices/addNew";
import DeleteCompany from "./DeleteCompany";
import { HistoryDialog } from "../../components/HistoryDialog";
import { CommentsDialog } from "../../components/CommentsDialog";

type CompaniesTableProps = {
  data: CompanyEntry[];
  columns: configProps<"companies">["columns"];
  isLoading: boolean;
  page: number;
  rowsPerPage: number;
  onPage: (e: DataTablePageEvent) => void;
  totalRecords?: number;
  sortField: string;
  sortOrder: number;
  onSort: (e: DataTableSortEvent) => void;
};

export default function CompaniesTable({
  data,
  columns,
  isLoading,
  page,
  rowsPerPage,
  onPage,
  totalRecords,
  sortField,
  sortOrder,
  onSort,
}: CompaniesTableProps) {
  const dispatch = useAppDispatch();

  const [selectedHistoryRow, setSelectedHistoryRow] =
    useState<CompanyEntry | null>(null);
  const [selectedCommentsRow, setSelectedCommentsRow] =
    useState<CompanyEntry | null>(null);

  const newClickBody = React.useCallback(
    (rowData: CompanyEntry) => {
      return (
        <div className="flex-container">
          <Button
            size="small"
            icon="pi pi-pencil"
            text
            onClick={() => {
              dispatch(
                addNewSlice.actions.openAddNewDialog({
                  target: {
                    field: "company",
                    type: "company",
                    id: rowData.id,
                    initialValues: {
                      name: rowData.name,
                      registry_code: rowData.registryCode,
                      is_buyer: rowData.isBuyer,
                      is_supplier: rowData.isSupplier,
                      is_transporter: rowData.isTransporter,
                      legal_address: rowData.legalAddress,
                    },
                  },
                }),
              );
            }}
          />
          <Button
            size="small"
            icon="pi pi-history"
            text
            onClick={(e) => {
              e.stopPropagation();
              setSelectedHistoryRow(rowData);
            }}
          />
          <Button
            size="small"
            className="wide"
            icon="pi pi-comment"
            text={rowData.commentsCount === 0}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedCommentsRow(rowData);
            }}
          />
          <DeleteCompany rowData={rowData} />
        </div>
      );
    },

    [dispatch],
  );
  return (
    <>
      <DataTable
        value={data}
        dataKey="id"
        size="small"
        lazy
        paginator
        rows={rowsPerPage}
        first={page * rowsPerPage}
        totalRecords={totalRecords}
        onPage={onPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
        sortField={sortField}
        sortOrder={(sortOrder ?? 1) as 1 | -1}
        onSort={onSort}
        scrollable
        scrollHeight="75vh"
        emptyMessage={
          isLoading ? "Ładowanie lokalizacji..." : "Nie można załadować firm!"
        }
      >
        {columns
          .filter((entry) => entry !== undefined)
          .map((entry) => {
            return (
              <Column
                key={entry.field}
                field={entry.field}
                header={entry.header}
                sortable={entry.sortable ?? false}
                body={entry.body}
              />
            );
          })}
        <Column className="icon-column" body={newClickBody} />
      </DataTable>
      <HistoryDialog<"companies">
        rowData={selectedHistoryRow}
        onHide={() => setSelectedHistoryRow(null)}
        columns={columns}
      />
      <CommentsDialog<"companies">
        rowData={selectedCommentsRow}
        onHide={() => setSelectedCommentsRow(null)}
        columns={columns}
      />
    </>
  );
}
